import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Button, Container, Row, Col, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosInstance';
import { validateEmail } from '../../utils/validation';
import { trackEvent } from '../../services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from '../../services/analytics/analyticsEvents';

const RegisterTalentStep2MagicLink = ({ email, role, onBack }) => {
    const navigate = useNavigate();
    const [status, setStatus] = useState('sending'); // sending | sent | error
    const [error, setError] = useState('');
    const [cooldown, setCooldown] = useState(0);

    const sendLink = async (isResend = false) => {
        const val = (email || '').trim();

        if (!val) {
            setError('Email is missing.');
            setStatus('error');
            return;
        }

        if (!validateEmail(val)) {
            setError('Please enter a valid email.');
            setStatus('error');
            return;
        }

        try {
            setStatus('sending');
            setError('');

            await api.post('/api/auth/magic-link/send', {
                email: val,
                mode: 'register',
                role: role || 'student',
                redirect: '/register?magic=1',
                hasAgreedToTerms: true,
                isNative: Capacitor.isNativePlatform(),
            });

            trackEvent(
                isResend
                    ? ANALYTICS_EVENTS.REGISTRATION_MAGIC_LINK_RESENT
                    : ANALYTICS_EVENTS.REGISTRATION_MAGIC_LINK_SENT,
                {
                    role: role || 'student',
                    redirect: '/register?magic=1',
                }
            );

            setStatus('sent');
            setCooldown(30);
        } catch (err) {
            console.error('Register magic link send failed:', err.response?.data || err.message);
            setStatus('error');
            setError(err.response?.data?.message || 'Failed to send magic link.');
        }
    };

    useEffect(() => {
        sendLink();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setInterval(() => setCooldown((c) => c - 1), 1000);
        return () => clearInterval(t);
    }, [cooldown]);

    return (
        <main className="main-content flex-grow-1 ms-0">
            <Container>
                <Row>
                    <Col xl={4} className="offset-xl-4 pt-5">
                        <Card>
                            <Card.Body>
                                <div className="mb-4">
                                    <h2 className="mb-2">Check your email</h2>
                                    <p className="mb-0">
                                        We sent a verification link to:
                                    </p>
                                    <p className="fw-semibold mb-0">{email}</p>
                                </div>

                                <div className="mb-4">
                                    <ul className="mb-0 font-size-md">
                                        <li>Link is single-use</li>
                                        <li>Expires in 20 minutes</li>
                                        <li>Open it on this device to continue</li>
                                    </ul>
                                </div>

                                {status === 'sending' && (
                                    <div className="mb-3">Sending link…</div>
                                )}

                                {status === 'error' && (
                                    <div className="mb-3" style={{ color: 'crimson' }}>
                                        {error}
                                    </div>
                                )}

                                <Button
                                    type="button"
                                    variant="primary w-100 justify-content-center"
                                    disabled={status === 'sending' || cooldown > 0}
                                    onClick={() => sendLink(true)}
                                >
                                    {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend link'}
                                </Button>

                                <Row className="mt-3">
                                    <Col xs={12} className="text-center">
                                        <Button
                                            type="button"
                                            variant="outline-secondary w-100 justify-content-center"
                                            onClick={typeof onBack === 'function' ? onBack : () => navigate('/')}
                                        >
                                            Back
                                        </Button>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </main>
    );
};

export default RegisterTalentStep2MagicLink;